import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { LogoC } from '@/components/brand/LogoMarks';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'hsl(220 18% 4%)' }}>
      {/* ── Left brand panel (desktop only) ─────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-between w-5/12 p-12 relative overflow-hidden"
        style={{ background: 'hsl(216 40% 7%)', borderRight: '1px solid hsl(var(--border))' }}>
        {/* Glow orbs */}
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full pointer-events-none"
          style={{ background: 'hsl(var(--primary) / 0.1)', filter: 'blur(70px)' }} />
        <div className="absolute bottom-16 right-0 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'hsl(var(--accent) / 0.07)', filter: 'blur(55px)' }} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <LogoC size={36} />
          <span className="font-semibold text-lg tracking-tight" style={{ color: 'hsl(var(--foreground))' }}>Reelia</span>
        </div>

        {/* Hero copy */}
        <div className="relative z-10">
          <p className="text-xs font-semibold tracking-widest uppercase mb-5"
            style={{ color: 'hsl(var(--primary))' }}>Content OS for Creators</p>
          <h1 className="text-4xl font-bold leading-tight mb-7"
            style={{ color: 'hsl(var(--foreground))' }}>
            Turn long videos<br />into viral content<br />
            <span style={{ color: 'hsl(var(--primary))' }}>in minutes.</span>
          </h1>
          <div className="space-y-3.5">
            {[
              'Smart viral moment detection',
              'AI-powered subtitle generation',
              'Multi-platform publishing',
            ].map(f => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'hsl(var(--primary) / 0.12)', border: '1px solid hsl(var(--primary) / 0.2)' }}>
                  <Zap size={11} style={{ color: 'hsl(var(--primary))' }} />
                </div>
                <span className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trial badge */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{ background: 'hsl(var(--primary) / 0.08)', border: '1px solid hsl(var(--primary) / 0.2)', color: 'hsl(var(--primary))' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
            7-day free trial · No credit card required
          </div>
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-10 lg:hidden">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'hsl(var(--primary))' }}>
              <span className="text-white font-bold text-lg">R</span>
            </div>
            <span className="font-semibold text-xl" style={{ color: 'hsl(var(--foreground))' }}>Reelia</span>
          </div>

          <h2 className="text-2xl font-bold mb-1.5" style={{ color: 'hsl(var(--foreground))' }}>
            Welcome back
          </h2>
          <p className="text-sm mb-8" style={{ color: 'hsl(var(--foreground-muted))' }}>
            Sign in to your workspace
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1.5"
                style={{ color: 'hsl(var(--foreground-muted))' }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="reelia-input w-full h-11"
                autoComplete="email"
                data-testid="login-email-input"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5"
                style={{ color: 'hsl(var(--foreground-muted))' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="reelia-input w-full h-11 pr-10"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'hsl(var(--foreground-subtle))' }}
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold mt-1"
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Iniciando sesión…' : 'Login'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: '1px solid hsl(var(--border))' }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 text-xs" style={{ background: 'hsl(220 18% 4%)', color: 'hsl(var(--foreground-subtle))' }}>
                new to reelia?
              </span>
            </div>
          </div>

          <Link to="/onboarding">
            <Button
              variant="ghost"
              className="w-full h-11 text-sm"
              style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}
              data-testid="create-account-btn"
            >
              Create your account →
            </Button>
          </Link>

          <p className="text-center text-xs mt-6" style={{ color: 'hsl(var(--foreground-subtle))' }}>
            7-day free trial · No credit card required
          </p>
        </div>
      </div>
    </div>
  );
}
